import { get, writable } from 'svelte/store';
import { fetchWithSession } from '../util/fetchWithSession';

export function createRecordingStore() {
  const store = writable({
    processes: [],
    subprocesses: [],
    tasks: [],
    subtasks: [],
    listOfSubprocesses: [],
    listOfTasks: [],
    listOfSubtasks: [],
    listOfRecordings: [],
    listOfTeams: [],
    _initialized: false,
    _loadedIdproject: null,
    _loadedFrameworkProject: null,
  });

  const { subscribe, update } = store;

  async function getRecordings(idproject) {
    const response = await fetchWithSession(
      `/api/damprojects/recordings?idproject=${idproject}`,
      { cache: "no-store" },
    );
    const data = await response.json();
    const teamMap = new Map();
    data.forEach(recording => {
      if (recording.team_id && !teamMap.has(recording.team_id)) {
        teamMap.set(recording.team_id, { team: recording.team, team_id: recording.team_id });
      }
    });
    const listOfTeams = Array.from(teamMap.values());
    update(state => ({ ...state, listOfRecordings: data, listOfTeams, _loadedIdproject: idproject }));
  }

  async function getProcesses() {
    const response = await fetchWithSession("/api/damprojects/processes");
    const data = await response.json();
    update(state => ({ ...state, processes: data }));
  }

  async function getSubprocesses() {
    const response = await fetchWithSession("/api/damprojects/subprocesses");
    const data = await response.json();
    const { processes } = get(store);
    const listOfSubprocesses = data.map(subprocess => ({
      ...subprocess,
      process: processes.find(proc => proc.id === subprocess.process_id)?.name,
    }));
    update(state => ({ ...state, subprocesses: data, listOfSubprocesses }));
  }

  async function getTasks() {
    const response = await fetchWithSession("/api/damprojects/tasks");
    const data = await response.json();
    const { subprocesses } = get(store);
    const listOfTasks = data.map(task => ({
      ...task,
      subprocess: subprocesses.find(sp => sp.id === task.subprocess_id)?.name,
    }));
    update(state => ({ ...state, tasks: data, listOfTasks }));
  }

  async function getSubTasks() {
    const response = await fetchWithSession("/api/damprojects/subtasks");
    const data = await response.json();
    const { tasks } = get(store);
    const listOfSubtasks = data.map(subtask => ({
      ...subtask,
      task: tasks.find(t => t.id === subtask.task_id)?.name,
    }));
    update(state => ({ ...state, subtasks: data, listOfSubtasks }));
  }

  async function getProjectFramework(idproject) {
    const response = await fetchWithSession(
      `/api/damprojects/project-collection/${idproject}`,
      { cache: "no-store" },
    );
    const collection = await response.json();
    if (!response.ok) throw new Error(collection.message || "Collection scope could not be loaded.");
    if (!Array.isArray(collection.subtask_ids) || collection.subtask_ids.some((id) => typeof id !== "string")) {
      throw new Error("Invalid collection scope response.");
    }
    const subtaskIds = new Set(collection.subtask_ids);
    const {
      processes,
      subprocesses,
      tasks,
      subtasks,
      listOfSubprocesses,
      listOfTasks,
      listOfSubtasks,
    } = get(store);
    const projectSubtasks = subtasks.filter((subtask) =>
      subtaskIds.has(String(subtask.id)),
    );
    const taskIds = new Set(projectSubtasks.map((subtask) => String(subtask.task_id)));
    const projectTasks = tasks.filter((task) =>
      taskIds.has(String(task.id)),
    );
    const subprocessIds = new Set(projectTasks.map((task) => String(task.subprocess_id)));
    const projectSubprocesses = subprocesses.filter((subprocess) =>
      subprocessIds.has(String(subprocess.id)),
    );
    const processIds = new Set(projectSubprocesses.map((subprocess) => String(subprocess.process_id)));
    const projectProcesses = processes.filter((process) =>
      processIds.has(String(process.id)),
    );

    update(state => ({
      ...state,
      processes: projectProcesses,
      subprocesses: projectSubprocesses,
      tasks: projectTasks,
      subtasks: projectSubtasks,
      listOfSubprocesses: listOfSubprocesses.filter((subprocess) =>
        subprocessIds.has(String(subprocess.id)),
      ),
      listOfTasks: listOfTasks.filter((task) =>
        taskIds.has(String(task.id)),
      ),
      listOfSubtasks: listOfSubtasks.filter((subtask) =>
        subtaskIds.has(String(subtask.id)),
      ),
      _loadedFrameworkProject: idproject,
    }));
  }

  async function initialize(idproject = null, { force = false } = {}) {
    const { _initialized, _loadedFrameworkProject } = get(store);
    if (
      !force && _initialized &&
      ((idproject && _loadedFrameworkProject === idproject) ||
        (!idproject && !_loadedFrameworkProject))
    ) {
      return;
    }

    await getProcesses();
    await getSubprocesses();
    await getTasks();
    await getSubTasks();
    if (idproject) await getProjectFramework(idproject);
    update(state => ({
      ...state,
      _initialized: true,
      _loadedFrameworkProject: idproject || null,
    }));
  }

  return {
    subscribe,
    initialize,
    getProcesses,
    getSubprocesses,
    getTasks,
    getSubTasks,
    getRecordings,
  };
}

// Until the self-run assigns a recording, it counts for the L4 it was recorded for.
// Only for counting views: the subtasks in the store keep their saved assignments.
export function withRecordedSubtasks(subtasks, recordings) {
  const assigned = new Set(
    (subtasks || []).flatMap((subtask) =>
      (subtask.recordings || []).map((recording) => recording.recording_id),
    ),
  );
  const recordedBySubtask = new Map();
  (recordings || []).forEach((recording) => {
    const recordingId = recording.recording_id ?? recording.recorderid;
    if (!recording.subtask_id || assigned.has(recordingId)) return;
    const key = String(recording.subtask_id);
    if (!recordedBySubtask.has(key)) recordedBySubtask.set(key, []);
    recordedBySubtask.get(key).push({ recording_id: recordingId });
  });
  if (!recordedBySubtask.size) return subtasks;
  return subtasks.map((subtask) => {
    const recorded = recordedBySubtask.get(String(subtask.id));
    return recorded
      ? { ...subtask, recordings: [...(subtask.recordings || []), ...recorded] }
      : subtask;
  });
}

export const recordingStore = createRecordingStore();
